import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { PopularQuery } from '../types/search';
import { useAuth } from '../components/auth/AuthContext';

export const useUserLikes = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: likedSearches = [], isLoading, error } = useQuery<PopularQuery[]>(
    ['userLikes', user?.id],
    async () => {
      if (!user?.id) return [];

      try {
        const { data: likes, error: likesError } = await supabase
          .from('user_likes')
          .select(`
            search_id,
            search:searches (
              id,
              query,
              searches,
              likes,
              created_at,
              last_searched_at
            )
          `)
          .eq('user_id', user.id);

        if (likesError) throw likesError;

        if (!likes) return [];

        return likes.map(like => ({
          id: like.search.id,
          query: like.search.query,
          searches: like.search.searches,
          likes: like.search.likes || 0,
          lastSearchedAt: like.search.last_searched_at,
          isLiked: true
        }));
      } catch (err) {
        console.error('Failed to fetch liked searches:', err);
        return [];
      }
    },
    {
      enabled: !!user?.id,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
    }
  );

  const { mutateAsync: toggleLike } = useMutation(
    async (searchId: string) => {
      if (!user?.id) {
        throw new Error('Must be logged in to like searches');
      }

      const { data } = await supabase
        .rpc('handle_search_like', {
          p_user_id: user.id,
          p_search_id: searchId
        });

      return { searchId, isLiked: data };
    },
    {
      onMutate: async (searchId) => {
        if (!user?.id) return;

        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['userLikes', user.id]);
        await queryClient.cancelQueries(['marketSearches', user.id]);
        await queryClient.cancelQueries('searches');
        
        // Snapshot the previous values
        const previousLikes = queryClient.getQueryData(['userLikes', user.id]);
        const previousSearches = queryClient.getQueryData(['marketSearches', user.id]);
        const previousAllSearches = queryClient.getQueryData('searches');
        
        // Optimistically update likes
        const willBeLiked = !likedSearchIds.includes(searchId);
        
        // Update liked search IDs
        queryClient.setQueryData(['userLikes', user.id], (old: PopularQuery[] = []) => {
          if (willBeLiked) {
            // Add the search if it's being liked
            const search = (previousAllSearches as any[])?.find(s => s.id === searchId);
            if (search) {
              return [...old, {
                ...search,
                likes: search.likes + 1
              }];
            }
          } else {
            // Remove the search if it's being unliked
            return old.filter(search => search.id !== searchId);
          }
          return old;
        });

        // Update market searches
        queryClient.setQueryData(['marketSearches', user.id], (old: any[] = []) => {
          return old.map(item => {
            if (item.search.id === searchId) {
              return {
                ...item,
                search: {
                  ...item.search,
                  likes: item.search.likes + (willBeLiked ? 1 : -1)
                }
              };
            }
            return item;
          });
        });

        // Update all searches
        queryClient.setQueryData('searches', (old: any[] = []) => {
          return old.map(search => {
            if (search.id === searchId) {
              return {
                ...search,
                likes: search.likes + (willBeLiked ? 1 : -1)
              };
            }
            return search;
          });
        });

        return { previousLikes, previousSearches, previousAllSearches };
      },
      onError: (err, searchId, context: any) => {
        if (user?.id && context) {
          queryClient.setQueryData(['userLikes', user.id], context.previousLikes);
          queryClient.setQueryData(['marketSearches', user.id], context.previousSearches);
          queryClient.setQueryData('searches', context.previousAllSearches);
        }
      },
      onSettled: () => {
        if (user?.id) {
          // Invalidate affected queries to ensure data consistency
          queryClient.invalidateQueries(['userLikes', user.id]);
          queryClient.invalidateQueries(['marketSearches', user.id]);
          queryClient.invalidateQueries('searches');
        }
      }
    }
  );

  const likedSearchIds = likedSearches.map(search => search.id);
  const isLiked = (searchId: string) => likedSearchIds.includes(searchId);

  return {
    likedSearches,
    likedSearchIds,
    isLiked,
    toggleLike,
    isLoading
  };
};